import React, { useRef, useState } from "react";
import { Col } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { Button, ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { PopoverController } from '@amzn/stencil-react-components/popover';
import { IconMoreHorizontal } from '@amzn/stencil-react-components/icons';
import { upsertEventModel } from "src/service/service";
import { APICallStatus, EventModelStatus, Stage } from "src/enum/enums";
import { isUserEligibleForProdPromotion } from "src/util/commonUtil";
import "src/components/commonStyles.scss";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { useAppSelector } from "src/state/hooks";
import { overrideRetireFromProdForSolutionTypes} from "src/constant/constants";

const STRINGS_DISPLAYED = {
  more_options_icon_title: "More options",
  retire_from_beta_button: "Retire from beta",
  retire_from_prod_button: "Retire from prod",
}

type props = {
  isLiveEventModelPresentInBeta: boolean,
  isLiveEventModelPresentInProd: boolean,
}

/**
 * Renders a 'more options' button with a popover displaying the
 * options like retiring event model from beta or prod etc.
 * TODO: Add verification modal for retiring from prod.
 */
export const MoreOptionsButtonWithPopover = ({isLiveEventModelPresentInBeta, isLiveEventModelPresentInProd}: props) => {
  const eventModelState = useAppSelector(({eventModel}) => eventModel);

  const [isMoreOptionsPopoverOpen, setIsMoreOptionsPopoverOpen] = useState(false);
  const moreOptionsTarget = useRef(null);

  const [retireEventModelStatus, setRetireEventModelStatus] = useState<Map<Stage, APICallStatus>>(
    new Map([
      [Stage.beta, APICallStatus.SUCCESSFUL], [Stage.prod, APICallStatus.SUCCESSFUL]
    ])
  );
  
  const onClickRetireButton = (stage: Stage) => {
    setRetireEventModelStatus(prevState => {
      prevState.set(stage, APICallStatus.IN_PROGRESS);
      return new Map(prevState);
    });
    upsertEventModel(eventModelState, EventModelStatus.RETIRED, stage)
      .then((_) => {
        setRetireEventModelStatus(prevState => {
          prevState.set(stage, APICallStatus.SUCCESSFUL);
          return new Map(prevState);
        });
      })
      .catch((_) => {
        setRetireEventModelStatus(prevState => {
          prevState.set(stage, APICallStatus.FAILED);
          return new Map(prevState);
        });
      });
  }

  const isRetireFromBetaEnabled = () => {
    return Boolean(isLiveEventModelPresentInBeta)
      && retireEventModelStatus.get(Stage.beta) !== APICallStatus.IN_PROGRESS;
  }

  const isRetireFromProdEnabled = () => {
    return Boolean(isLiveEventModelPresentInProd)
      && retireEventModelStatus.get(Stage.prod) !== APICallStatus.IN_PROGRESS
      && (isUserEligibleForProdPromotion() || overrideRetireFromProdForSolutionTypes.includes(eventModelState?.eventSolution?.type));
  }

  return <> 
    <Button
      variant={ButtonVariant.Secondary}
      icon={<IconMoreHorizontal title={STRINGS_DISPLAYED.more_options_icon_title}/>}
      className={"width_fit_content"}
      onClick={() => setIsMoreOptionsPopoverOpen(!isMoreOptionsPopoverOpen)}
      ref={moreOptionsTarget}
    />
    <PopoverController
      target={moreOptionsTarget}
      isOpen={isMoreOptionsPopoverOpen}
      close={() => setIsMoreOptionsPopoverOpen(false)}
    >
      <Col padding={"1px"}>
        <Button
          size={ButtonSize.Small}
          variant={ButtonVariant.Tertiary}
          isDestructive={true}
          onClick={() => {
            onClickRetireButton(Stage.beta)
          }}
          disabled={!isRetireFromBetaEnabled()}
        >
          <Text fontSize={"16px"}>{STRINGS_DISPLAYED.retire_from_beta_button}</Text>
          {(retireEventModelStatus.get(Stage.beta) === APICallStatus.IN_PROGRESS) && <Spinner />}
        </Button>

        <Button
          size={ButtonSize.Small}
          variant={ButtonVariant.Tertiary}
          isDestructive={true}
          onClick={() => {
            onClickRetireButton(Stage.prod)
          }}
          disabled={!isRetireFromProdEnabled()}
        >
          <Text fontSize={"16px"}>{STRINGS_DISPLAYED.retire_from_prod_button}</Text>
          {(retireEventModelStatus.get(Stage.prod) === APICallStatus.IN_PROGRESS) && <Spinner />}
        </Button>
      </Col>
    </PopoverController>
  </>
}
